/**
 * Look-ahead reserve for opportunistic debt repayment.
 *
 * Retain SPV cash so that future known payments stay covered after accounting for
 * the *amounts* of confirmed receipts — including bills due in a receipt month.
 * A tiny confirmed inflow must not unlock a large repayment.
 *
 * Confirmed: capital_call events, land facility (land-cost months),
 * delivery usable buyer cash.
 * Not confirmed: capital-matched drips (removed), construction facility draws,
 * projected credit, future sales deposits, restricted deposits.
 */

import { n } from './math.js';
import { costsPaidInMonth } from './buildCostSchedule.js';
import { resolveSecuredFacilities } from './strategies/bankFacilities.js';

function deliveryBuyerCashEstimate(pnl, assumptions) {
  const sales = Math.max(0, n(pnl.salesRevenue));
  const depositRate = Math.max(0, n(assumptions.depositRate));
  const usablePct = Math.max(0, n(assumptions.depositUsablePct));
  const totalDeposits = sales * depositRate;
  const alreadyUsable = totalDeposits * usablePct;
  const remainingSaleCash = Math.max(0, sales - totalDeposits);
  return remainingSaleCash + (totalDeposits - alreadyUsable);
}

/**
 * Confirmed receipt amount for a future month under a simulated draw state.
 * Returns { receipt, state } with updated facility drawn totals.
 */
export function estimateConfirmedReceiptAmount({
  month,
  programme,
  costSchedule,
  assumptions,
  pnl,
  simState,
  capitalCalls = {},
}) {
  let receipt = Math.max(0, n(capitalCalls[month]));
  const next = {
    landFacilityDrawn: n(simState.landFacilityDrawn),
    sharedFacilityDrawn: n(simState.sharedFacilityDrawn),
    securedConstructionFacilityDrawn: n(simState.securedConstructionFacilityDrawn),
  };

  const costs = costsPaidInMonth(costSchedule, month - 1);
  const landCost = n(costs.detail.landPrice) + n(costs.detail.acquisitionCosts);

  const facilities = resolveSecuredFacilities(pnl);
  const sharedHeadroom = Math.max(
    0,
    facilities.sharedBankFacility - next.sharedFacilityDrawn,
  );
  const landHeadroom =
    Math.max(0, facilities.landSecured - next.landFacilityDrawn) + sharedHeadroom;

  if (landCost > 1e-9 && landHeadroom > 1e-9) {
    const draw = Math.min(landCost, landHeadroom);
    receipt += draw;
    const fromShared = Math.min(draw, sharedHeadroom);
    next.sharedFacilityDrawn += fromShared;
    next.landFacilityDrawn += draw - fromShared;
  }

  if (month === programme.deliveryMonth) {
    receipt += deliveryBuyerCashEstimate(pnl, assumptions);
  }

  return { receipt, state: next };
}

/**
 * Cash that must stay in the SPV after opportunistic repayment:
 * minCashBuffer + enough to keep a forward simulation non-negative when applying
 * confirmed receipt *amounts* and known payments (including the receipt month).
 */
export function computeRepaymentRunwayReserve({
  afterMonth,
  programme,
  costSchedule,
  assumptions,
  pnl,
  state,
  capitalCalls = {},
}) {
  const buffer = Math.max(0, n(assumptions.minCashBuffer));
  const totalMonths = Math.max(0, programme.totalMonths || 0);
  if (afterMonth >= totalMonths) return buffer;

  let simState = {
    landFacilityDrawn: n(state.landFacilityDrawn),
    sharedFacilityDrawn: n(state.sharedFacilityDrawn),
    securedConstructionFacilityDrawn: n(state.securedConstructionFacilityDrawn),
  };

  let running = 0;
  let minRunning = 0;

  for (let month = afterMonth + 1; month <= totalMonths; month += 1) {
    const costs = costsPaidInMonth(costSchedule, month - 1);
    const { receipt, state: nextState } = estimateConfirmedReceiptAmount({
      month,
      programme,
      costSchedule,
      assumptions,
      pnl,
      simState,
      capitalCalls,
    });
    simState = nextState;

    running += receipt - Math.max(0, costs.total);
    if (running < minRunning) minRunning = running;
  }

  return buffer + Math.max(0, -minRunning);
}
